import React, { useEffect, useMemo, useState } from 'react';
import { Pagination, Tag } from 'antd';

import { RecordIdentity, RecordTitle } from '../components/common/RecordBadge';
import ItemCardsGrid from '../components/items/ItemCardsGrid';
import ItemReadView from '../components/items/ItemReadView';
import PublicAttachments from '../components/common/PublicAttachments';
import { PublicFilesProvider } from '../components/common/PublicFiles';
import ReportPageLink from '../components/common/ReportPageLink';
import { PublicTilesSkeleton } from '../components/common/PublicPageSkeleton';
import { DataState, deriveDataState } from '../components/loading';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getSharedItems } from '../services/shareLinks';
import { createElementResolver, createPrimaryDateResolver, indexCardsById, indexElementSetsById } from '../utils/cards';
import { formatDisplayDate } from '../utils/dateDisplay';
import { normalizeId } from '../utils/ids';
import getItemDisplayValue from '../utils/items';
import { createTitleMapResolver } from '../utils/lookups';
import { getDictionaryFromName } from '../utils/dictionary';

const PAGE_SIZE = 20;

const KIND_META = {
	view: {
		badge: 'Shared view',
		fallbackName: 'Shared view',
		note: 'A read-only view of selected items. Nothing here can be edited, and the '
			+ 'link stops working when it expires.',
	},
	item: {
		badge: 'Shared item',
		fallbackName: 'Shared item',
		note: 'A read-only copy of one record. Nothing here can be edited, and the '
			+ 'link stops working when it expires.',
	},
	card: {
		badge: 'Shared card',
		fallbackName: 'Shared card',
		note: 'A read-only look at how this card is built. It shows the template, not '
			+ 'anybody\'s records, and the link stops working when it expires.',
	},
};

/**
 * The public shared view: what somebody holding a temporary share link sees,
 * with no account and no session.
 *
 * Everything it renders comes from the two unauthenticated share endpoints —
 * the descriptor from `GET /api/share/{code}` (collection name, the card
 * definitions needed to render, the link's own name and expiry, and for the
 * pinned kinds the target itself), and, for a view link only, the items a page
 * at a time from `GET /api/share/{code}/items`. The card definitions arrive in
 * the payload precisely so this page never has to touch
 * CardDefinitionsContext, whose fetch is authenticated.
 *
 * Three kinds of target answer here, and the link's `kind` picks the body:
 * a list of items, one pinned item, or one card definition. A link stored
 * before kinds existed has none, and reads as 'view'.
 *
 * There is deliberately nothing to click, whichever kind it is: no item links,
 * no share, no audience window, no relate. The link is a window, not a door.
 *
 * TAGS (T7; docs/plans/tags.md §3.5, ruled d24): each item carries only the
 * tags this page discloses — a collection tag that is not restricted, an item
 * tag whose item the page could publish — with their names in `tagTitles`.
 * The item and the tiles show exactly those, read-only: no picker, no ✕, no
 * tag page links, no private tags, no "Why", and nothing for an id with no
 * name (utils/itemTags.js publicTagEntries).
 *
 * FILES (PR 10c; the 10c contract): on a view or item link that includes
 * files, each item carries `files` facts for its file elements' values, and
 * an item link's item its Other attachments in `attachments`. The page
 * provides PublicFilesProvider, so the grid draws each photo's inline `sm`
 * (no byte request per tile), the item shows `md` and opens `lg` full screen,
 * and Download — or "Sign in to download" for a type visitors may not take —
 * reads the public routes with no Authorization header. A link without files
 * sends no facts, and the file elements are not shown.
 *
 * `onDead` fires when a page fetch answers the flat 404 — the link expired or
 * was revoked while the visitor was reading — so the caller can swap in the
 * invalid-link screen. Only the view body can raise it; the pinned kinds are
 * fully described by the descriptor and make no second call (asking the items
 * endpoint about them is a 404 by contract, which would kill a live link).
 */
// The dictionary's word for an element's type, never the raw dataType slug.
// An unset or unknown type IS short text everywhere else on the platform (see
// CardDefElement), so it reads that way here too.
const elementTypeLabel = def => (getDictionaryFromName(def?.dataType) || getDictionaryFromName('text-small')).label;

export default function SharedViewPage({ code, view, onDead }) {
	const kind = view?.kind || 'view';
	const meta = KIND_META[kind] || KIND_META.view;

	const cardsById = useMemo(() => indexCardsById(view?.cards || []), [view]);
	// `elementSets` rides beside `cards` in every share payload: the sets the
	// cards attach, so a set's elements resolve here as they do behind
	// authentication.
	const elementSetsById = useMemo(() => indexElementSetsById(view?.elementSets || []), [view]);
	const getElementsForCard = useMemo(() => createElementResolver(cardsById, elementSetsById), [cardsById, elementSetsById]);
	// Each card's primary date with its sets folded in, as the cache resolves it.
	const resolvePrimaryDate = useMemo(() => createPrimaryDateResolver(cardsById, elementSetsById), [cardsById, elementSetsById]);

	const collection = view?.collection || null;
	const expires = view?.expiresAt ? formatDisplayDate(view.expiresAt) : null;
	// The browser tab names what the page shows: a view link its collection;
	// an item or card link names its record from the body below.
	useDocumentTitle(kind === 'view' ? (collection?.name || 'Shared items') : '');

	return (
		<div className="shared-view">
			<header className="shared-view__header">
				<RecordIdentity
					kicker={<Tag className="shared-view__badge">{meta.badge}</Tag>}
					badge={(
						<RecordTitle
							kind="collection"
							record={collection}
							title={collection?.name || 'Shared items'}
							size="xl"
							wrap
							titleAs="h1"
						/>
					)}
					below={(
						<>
							<span>{view?.name || meta.fallbackName}</span>
							{expires ? (
								<span className="shared-view__expiry">Expires {expires}</span>
							) : null}
						</>
					)}
				/>
			</header>

			<p className="shared-view__note">{meta.note}</p>

			<PublicFilesProvider code={code}>
				{kind === 'item' ? (
					<SharedItemBody
						code={code}
						attachments={view?.item?.attachments}
						item={view?.item}
						cardsById={cardsById}
						getElementsForCard={getElementsForCard}
						lookupTitles={view?.lookupTitles}
					/>
				) : kind === 'card' ? (
					<SharedCardBody cards={view?.cards} getElementsForCard={getElementsForCard} />
				) : (
					<SharedItemsBody
						code={code}
						collectionId={collection?._id}
						cardsById={cardsById}
						resolvePrimaryDate={resolvePrimaryDate}
						getElementsForCard={getElementsForCard}
						onDead={onDead}
					/>
				)}
			</PublicFilesProvider>

			{/* G16: anyone may tell keepr's admins about a page. */}
			<footer className="shared-view__report">
				<ReportPageLink code={code} />
			</footer>
		</div>
	);
}

/** kind 'view' — the paginated grid, and the only body that fetches. */
function SharedItemsBody({ code, collectionId, cardsById, resolvePrimaryDate = null, getElementsForCard, onDead }) {
	const [items, setItems] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [lookupTitles, setLookupTitles] = useState({});
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	// A page that could not be had is a failure with Retry, never an empty grid.
	const [failed, setFailed] = useState(false);
	const [hasData, setHasData] = useState(false);
	const [attempt, setAttempt] = useState(0);

	// Same disclosure rule as the pinned-item body: lookups resolve only
	// through the titles the server sent with this page of items, and a
	// withheld id renders the em-dash rather than leaking the raw id (which
	// is what the pool-backed resolver ItemCardsGrid builds by default would
	// fall back to).
	const resolveLookupValue = useMemo(
		() => createTitleMapResolver(lookupTitles),
		[lookupTitles]
	);

	useEffect(() => {
		if (!code) return undefined;
		let active = true;
		setLoading(true);
		setFailed(false);
		getSharedItems(code, { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE })
			.then(({ items: pageItems, totalCount: total, lookupTitles: titles }) => {
				if (!active) return;
				setItems(pageItems);
				setTotalCount(total);
				setLookupTitles(titles || {});
				setHasData(true);
			})
			.catch(error => {
				if (!active) return;
				// Every public failure is the same flat 404: revoked, expired,
				// or a collection deleted since. All of them mean the same
				// thing to the visitor.
				if (error?.response?.status === 404 && onDead) onDead();
				else setFailed(true);
			})
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [code, page, onDead, attempt]);

	const state = deriveDataState({
		loading,
		error: failed ? 'failed' : null,
		hasData,
	});

	return (
		<DataState
			// A new page of a view already shown is a refresh. Retry starts the
			// wait afresh, with its own 30 s. (An empty list is still `ready`:
			// the grid says so itself, beside the footer.)
			key={attempt}
			state={state}
			hasData={hasData}
			skeleton={<PublicTilesSkeleton />}
			// Only wording written here is shown: never the server's message.
			error={null}
			onRetry={() => setAttempt(n => n + 1)}
			label="these items"
			className="data-state--public"
		>
			<ItemCardsGrid
				readOnly
				items={items}
				cardsById={cardsById}
				resolvePrimaryDate={resolvePrimaryDate}
				collectionId={collectionId}
				getElementsForCard={getElementsForCard}
				resolveLookupValue={resolveLookupValue}
				unitSystem="default"
				emptyText="No items are shared through this link."
				// T7: each tile's tags — the ones this page discloses, read-only.
				publicTags
			/>

			<div className="shared-view__footer">
				<span>
					{totalCount === 0
						? 'No items'
						: `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(totalCount, (page - 1) * PAGE_SIZE + items.length)} of ${totalCount}`}
				</span>
				<Pagination
					current={page}
					pageSize={PAGE_SIZE}
					total={totalCount}
					showSizeChanger={false}
					onChange={setPage}
				/>
			</div>
		</DataState>
	);
}

/**
 * kind 'item' — the one pinned record, laid out like the detail pane's field
 * list and stripped of every affordance that pane has.
 */
function SharedItemBody({ code, attachments = null, item, cardsById, getElementsForCard, lookupTitles }) {
	const cardId = normalizeId(item?.card_id);
	const card = cardsById[cardId] || null;
	const elementDefs = useMemo(
		() => (cardId ? getElementsForCard(cardId) : []),
		[cardId, getElementsForCard]
	);
	// Lookups resolve against the titles the descriptor disclosed — each a
	// referenced item's pre-rendered display title, gated server-side by the
	// same publishability rule as everything else on this page. An id the
	// server withheld resolves to the em-dash, never the raw id.
	const resolveLookupValue = useMemo(
		() => createTitleMapResolver(lookupTitles),
		[lookupTitles]
	);
	const title = useMemo(() => getItemDisplayValue({
		card,
		elementDefs,
		item: item || {},
		resolveLookupValue,
	}), [card, elementDefs, item, resolveLookupValue]);
	useDocumentTitle(item ? title : '');

	const created = item?.createdAt ? formatDisplayDate(item.createdAt) : null;

	if (!item) {
		return <div className="shared-view__empty">This record is no longer shared through this link.</div>;
	}

	return (
		<article className="shared-view__record">
			<RecordIdentity
				className="shared-view__record-title"
				badge={(
					<RecordTitle
						kind="item"
						record={card}
						kicker={card?.label?.singular || card?.name || 'Item'}
						title={title}
						size="lg"
						wrap
						titleAs="h2"
					/>
				)}
				below={created ? <span className="shared-view__expiry">Added {created}</span> : null}
			/>

			{/* The descriptor serves cards:[] when the item's card is not
			    itself publishable, which leaves nothing to lay the record out
			    with. Say that, rather than showing a record with no fields and
			    no explanation. The read view is the same one the app's own
			    pane renders, minus everything attached to the record. */}
			{elementDefs.length === 0 ? (
				<div className="shared-view__empty">{"This record's fields aren't available."}</div>
			) : (
				<ItemReadView
					item={item}
					card={card}
					elementDefs={elementDefs}
					resolveLookupValue={resolveLookupValue}
					showMeta={false}
					// T7: the tags this page discloses (non-restricted
					// collection tags, publishable item tags), read-only.
					itemTags="public"
					publicCardsById={cardsById}
					unitSystem="default"
					// A public reader has no session to ask for the card's
					// form layout with (and the share payload does not carry
					// one), so a shared record groups automatically.
					formLayout={null}
					// A "Me too" count reads alone: nobody here is signed in to
					// say it (KPR-390).
					confirmations={false}
				/>
			)}

			{/* Its Other attachments, when the link includes files (G13). */}
			<PublicAttachments code={code} attachments={attachments} />
		</article>
	);
}

/**
 * kind 'card' — the template itself: what a record of this card is made of.
 * `cards` is the card plus its ancestors; the card being shared is the one no
 * other card in the payload claims as a parent.
 */
function SharedCardBody({ cards, getElementsForCard }) {
	const card = useMemo(() => {
		const list = Array.isArray(cards) ? cards : [];
		const leaf = list.find(candidate => !list.some(
			other => normalizeId(other?.parentCardId) === normalizeId(candidate?._id)
		));
		return leaf || list[0] || null;
	}, [cards]);

	const elementDefs = useMemo(
		() => (card?._id ? getElementsForCard(card._id) : []),
		[card, getElementsForCard]
	);
	useDocumentTitle(card ? (card.name || 'Untitled card') : '');

	if (!card) {
		return <div className="shared-view__empty">This card is no longer shared through this link.</div>;
	}

	return (
		<article className="shared-view__record">
			<RecordIdentity
				className="shared-view__record-title"
				kicker="Card definition"
				badge={(
					<RecordTitle
						kind="card"
						record={card}
						title={card?.name || 'Untitled card'}
						size="lg"
						wrap
						titleAs="h2"
					/>
				)}
				below={card?.description ? <span>{card.description}</span> : null}
			/>

			<div className="item-read__fields">
				{elementDefs.length === 0 ? (
					<div className="shared-view__empty">This card has no fields yet.</div>
				) : elementDefs.map(def => (
					<div className="item-pane__field" key={def?.name}>
						<div className="micro-label">{def?.label?.singular || def?.name}</div>
						<div className="item-pane__value">
							{elementTypeLabel(def)}
							{def?.options?.required ? <Tag className="shared-view__badge">Required</Tag> : null}
						</div>
					</div>
				))}
			</div>
		</article>
	);
}
